import { useMemo } from 'react'
import { useQueries } from '@tanstack/react-query'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import type { ApiResponse } from '@/api/http/types'
import { useSessionStore } from '@/stores/useSessionStore'
import {
  metricsFromInstruments,
  type CampaignListInstrumentMetrics,
} from '../lib/campaign-list-metrics'
import type { CampaignInstrument } from '../types'

async function fetchCampaignInstruments(campaignId: string): Promise<CampaignInstrument[]> {
  const { data: envelope } = await apiClient.get<ApiResponse<CampaignInstrument[]>>(
    `/api/v1/campaigns/${campaignId}/instruments`,
  )
  return envelope.data
}

export function useCampaignListInstrumentMetrics(campaignIds: string[]): {
  metricsByCampaignId: Map<string, CampaignListInstrumentMetrics>
  isLoading: boolean
} {
  const tenantId = useSessionStore((s) => s.user?.tenantId)
  const tenantKey = tenantId ?? '_'
  const uniqueIds = useMemo(
    () => [...new Set(campaignIds.filter((id) => id.length > 0))],
    [campaignIds],
  )

  const results = useQueries({
    queries: uniqueIds.map((campaignId) => ({
      queryKey: queryKeys.campaigns.instruments(tenantKey, campaignId),
      queryFn: () => fetchCampaignInstruments(campaignId),
      enabled: Boolean(campaignId),
      staleTime: 30_000,
    })),
  })

  const metricsByCampaignId = useMemo(() => {
    const map = new Map<string, CampaignListInstrumentMetrics>()
    uniqueIds.forEach((campaignId, index) => {
      const instruments = results[index]?.data
      if (instruments) {
        map.set(campaignId, metricsFromInstruments(instruments))
      }
    })
    return map
  }, [uniqueIds, results])

  return {
    metricsByCampaignId,
    isLoading: results.some((result) => result.isLoading),
  }
}
